import { Link } from "@tanstack/react-router";
import { Instagram, Facebook, Youtube } from "lucide-react";
import { XLogo } from "@/components/site/icons/XLogo";

import { LanguageSwitcher } from "@/components/site/LanguageSwitcher";
import { ES_BLOG_PREFIX, ES_FOOTER_GROUPS, ES_PAGES, type EsPageKey } from "@/lib/es-pages";

// Same tap-target rule as the English footer: vertical padding only below the
// sm breakpoint, so phones get ~40px targets and desktop stays unchanged.
const linkClass = "inline-block py-2.5 sm:py-0 text-brand-deep hover:underline";
const headingClass = "text-sm font-bold uppercase tracking-wide text-brand-deep";

/**
 * One footer link. Blog articles live on a dynamic route, so they pass the
 * slug as a param; every other Spanish page is a static route path.
 */
function EsFooterLink({ pageKey }: { pageKey: EsPageKey }) {
  const page = ES_PAGES[pageKey];
  const shared = { hrefLang: "es", lang: "es", className: linkClass } as const;

  if (page.es.startsWith(ES_BLOG_PREFIX)) {
    return (
      <Link
        to="/es/el-doctor-analiza-las-noticias/$slug"
        params={{ slug: page.es.slice(ES_BLOG_PREFIX.length) }}
        {...shared}
      >
        {page.label}
      </Link>
    );
  }

  // Blog article paths are handled above, so drop them from the union to
  // leave only static Spanish route paths that <Link to> accepts.
  const to = page.es as Exclude<
    typeof page.es,
    `${typeof ES_BLOG_PREFIX}${string}`
  >;

  return (
    <Link to={to} {...shared}>
      {page.label}
    </Link>
  );
}

/**
 * Spanish footer, shown only on /es routes. Every link points at a page that
 * exists in Spanish (generated from ES_FOOTER_GROUPS), so Spanish visitors and
 * crawlers never leave the Spanish section through the footer.
 */
export function EsFooter() {
  return (
    <footer lang="es" className="border-t border-border bg-background flex items-center">
      <div className="mx-auto w-full max-w-7xl px-6 py-6 flex flex-col gap-6">
        <p className="text-center text-xs text-muted-foreground">
          Atendemos el norte de Virginia y el área metropolitana de Washington, D.C. — incluyendo
          Herndon, Reston, Fairfax, Loudoun, Arlington, Alexandria y comunidades cercanas.
        </p>

        <nav
          aria-label="Pie de página en español"
          className="grid grid-cols-1 gap-6 text-sm sm:grid-cols-3"
        >
          {ES_FOOTER_GROUPS.map((group) => (
            <div key={group.label} className="flex flex-col items-start gap-2">
              <h2 className={headingClass}>{group.label}</h2>
              {group.items.map((key) => (
                <EsFooterLink key={key} pageKey={key} />
              ))}
            </div>
          ))}
        </nav>

        <p className="text-center text-sm font-bold">
          <a
            href="tel:7037099771"
            aria-label="Llame a Virginia Bariatric Surgery al 703-709-9771"
            className={`inline-block py-2.5 sm:py-0 whitespace-nowrap font-bold ${linkClass}`}
          >
            (703) 709-9771
          </a>
        </p>

        {/* Crawlable path back to the English counterpart of the current page. */}
        <p className="text-center text-sm text-muted-foreground">
          <LanguageSwitcher className={linkClass} />
        </p>
        <p className="text-center text-sm">
          <Link to="/app-privacy-policy" className={linkClass}>Política de privacidad del sitio (en inglés)</Link>
        </p>

        <div className="flex items-center justify-center gap-4 pt-1">
          <a
            href="https://www.instagram.com/doctorfitzer/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Siga al Dr. Matthew Fitzer en Instagram"
            className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-full p-2 text-brand-deep hover:bg-brand-soft/40 transition-colors"
          >
            <Instagram size={20} aria-hidden="true" />
          </a>
          <a
            href="https://www.facebook.com/virginiabariatric/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Visite Virginia Bariatric Surgery en Facebook"
            className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-full p-2 text-brand-deep hover:bg-brand-soft/40 transition-colors"
          >
            <Facebook size={20} aria-hidden="true" />
          </a>
          <a
            href="https://www.youtube.com/@virginiabariatricsurgeryce114"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Visite Virginia Bariatric Surgery en YouTube"
            className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-full p-2 text-brand-deep hover:bg-brand-soft/40 transition-colors"
          >
            <Youtube size={20} aria-hidden="true" />
          </a>
          <a
            href="https://x.com/vbariatric"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Siga a Virginia Bariatric en X (Twitter)"
            className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-full p-2 text-brand-deep hover:bg-brand-soft/40 transition-colors"
          >
            <XLogo size={18} />
          </a>
        </div>

        <img
          src="/logo.svg"
          alt="Logotipo de Virginia Bariatric Surgery"
          width={1500}
          height={321}
          loading="lazy"
          decoding="async"
          className="mx-auto mt-2 h-5 w-auto sm:h-6 dark:brightness-[1.55]"
        />
        <p className="text-center text-xs text-muted-foreground pt-2">
          © 2026 Virginia Bariatric Surgery, P.C. Todos los derechos reservados.
        </p>
      </div>
    </footer>
  );
}
